import { CreateParams, UpdateParams } from 'react-admin';

import { CREATE_UPDATE_TYPES, imageResources, saveResourceWithMultipartData } from '@app/utils/imageResource';
import { Dayjs } from 'dayjs';
import simpleRestProvider from 'ra-data-simple-rest';

import httpClient from '@app/config/clients/http';

const throwException = (err: Error) => {
  throw err;
};

interface ReorderParams {
  id: number;
  body: number[];
}

interface EpgParams {
  data: any;
  date_target: string;
}

interface EpgCopyParams {
  date_source: Dayjs;
  date_target: Dayjs;
}

const cmsApiBaseUrl = window.ENV.API_URL ?? throwException(new Error('REACT_APP_API_URL is not set'));

const standardDataProvider = simpleRestProvider(cmsApiBaseUrl, httpClient, 'X-Total-Count');

const cmsApiDataProvider = {
  ...standardDataProvider,
  create: async (resource: string, params: CreateParams) => {
    const imgResource = imageResources.find((imgRsrc) => imgRsrc.id === resource);

    if (!imgResource) {
      // fallback to the default implementation
      return standardDataProvider.create(resource, params);
    }

    return saveResourceWithMultipartData(imgResource, CREATE_UPDATE_TYPES.POST, resource, params);
  },

  update: async (resource: string, params: UpdateParams) => {
    const imgResource = imageResources.find((imgRsrc) => imgRsrc.id === resource);

    if (!imgResource) {
      // fallback to the default implementation
      return standardDataProvider.update(resource, params);
    }

    return saveResourceWithMultipartData(imgResource, CREATE_UPDATE_TYPES.PUT, resource, params);
  },
  fetchSubTaxonomies: async (resource: string, taxonomyId: number) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}/${taxonomyId}/${resource}`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json };
    });
  },
  fetchCategoryTopics: async (resource: string, categoryId: number) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}/${categoryId}/topics`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json };
    });
  },
  reorderSubTaxonomies: async (resource: string, params: ReorderParams) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}/${params.id}/reorder`, {
      method: 'PUT',
      body: JSON.stringify(params.body),
    }).then((response) => {
      return { data: response.json };
    });
  },
  reorder: async (resource: string, params: ReorderParams) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}`, {
      method: 'PATCH',
      body: JSON.stringify({ ids: params.body }),
    }).then((response) => {
      return { data: response.json };
    });
  },
  fetchLinks: async (resource: string, resourceId: number) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}/${resourceId}/links`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json };
    });
  },
  fetchSegments: async (resource: string) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    return httpClient(`${baseUrl}`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json };
    });
  },
  fetchEpgByDate: async (resource: string, date: string) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}?filter=${encodeURIComponent('{')}"date"${encodeURIComponent(
      `:"${date}"`
    )}${encodeURIComponent('}')}`;

    return httpClient(`${baseUrl}`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json };
    });
  },
  saveEpg: async (resource: string, params: EpgParams) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}`;

    const payload = {
      epgs: params.data,
      date_target: params.date_target,
    };

    return httpClient(`${baseUrl}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then((response) => {
      return { data: response.json };
    });
  },
  copyEpgSchedule: async (resource: string, params: EpgCopyParams) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}/copy`;

    const payload = {
      date_source: params.date_source,
      date_target: params.date_target,
    };

    return httpClient(`${baseUrl}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((response) => {
      return { data: response.json };
    });
  },
  fetchEventByBMID: async (resource: string, id: string) => {
    const baseUrl = `${cmsApiBaseUrl}/${resource}/conferences/${id}`;

    return httpClient(`${baseUrl}`, {
      method: 'GET',
    }).then((response) => {
      return { data: response.json.conference };
    });
  },
};

export default cmsApiDataProvider;
